import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Edit,
  Trash2,
  Settings,
  FileText,
  Eye,
  EyeOff,
  GripVertical,
  Copy,
  ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: "texto", label: "Texto" },
  { value: "numero", label: "Número" },
  { value: "data", label: "Data" },
  { value: "selecao", label: "Seleção" },
  { value: "checkbox", label: "Checkbox" }
];

const DEFAULT_STANDARD_FIELDS = {
  nome_completo: { label: "Nome Completo", visivel: true, obrigatorio: true },
  email: { label: "Email", visivel: true, obrigatorio: true },
  telefone: { label: "Telefone", visivel: true, obrigatorio: true },
  endereco: { label: "Endereço", visivel: true, obrigatorio: true },
  cidade: { label: "Cidade", visivel: true, obrigatorio: true },
  sexo: { label: "Sexo", visivel: true, obrigatorio: true },
  data_nascimento: { label: "Data de Nascimento", visivel: true, obrigatorio: true },
  estado_civil: { label: "Estado Civil", visivel: true, obrigatorio: true },
  situacao: { label: "Situação", visivel: true, obrigatorio: true },
  batismo: { label: "Batismo", visivel: true, obrigatorio: true },
  ministerio_desejado: { label: "Deseja Servir em Ministério", visivel: true, obrigatorio: false }
};

export default function PublicFormConfigPage() {
  const { isAdmin, currentBranch, branches } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [customFieldDialog, setCustomFieldDialog] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);

  const [form, setForm] = useState({
    filial_id: "",
    nome_formulario: "Cadastro Público",
    ativo: true,
    campos_padrao: { ...DEFAULT_STANDARD_FIELDS },
    campos_personalizados: [],
    cidades_opcoes: ["Senador Canedo", "Goiânia", "Aparecida de Goiânia", "Outros"]
  });

  const [customFieldForm, setCustomFieldForm] = useState({
    id: "",
    label: "",
    tipo: "texto",
    obrigatorio: false,
    opcoes: [],
    ordem: 0
  });

  const [newCidade, setNewCidade] = useState("");

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await axios.get(`${API}/public-form-configs`);
      setConfigs(response.data);
    } catch (error) {
      console.error("Error loading configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.filial_id) {
      toast.error("Selecione uma unidade");
      return;
    }

    try {
      const payload = {
        ...form,
        campos_padrao: Object.fromEntries(
          Object.entries(form.campos_padrao).map(([key, val]) => [
            key,
            { visivel: val.visivel, obrigatorio: val.obrigatorio }
          ])
        )
      };

      if (editingConfig) {
        await axios.put(`${API}/public-form-configs/${editingConfig.id}`, payload);
        toast.success("Configuração atualizada!");
      } else {
        await axios.post(`${API}/public-form-configs`, payload);
        toast.success("Configuração criada!");
      }
      setDialogOpen(false);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar configuração");
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    
    // Merge default fields with saved config
    const mergedCamposPadrao = { ...DEFAULT_STANDARD_FIELDS };
    if (config.campos_padrao) {
      Object.keys(config.campos_padrao).forEach(key => {
        if (mergedCamposPadrao[key]) {
          mergedCamposPadrao[key] = {
            ...mergedCamposPadrao[key],
            ...config.campos_padrao[key]
          };
        }
      });
    }

    setForm({
      filial_id: config.filial_id,
      nome_formulario: config.nome_formulario || "Cadastro Público",
      ativo: config.ativo !== false,
      campos_padrao: mergedCamposPadrao,
      campos_personalizados: config.campos_personalizados || [],
      cidades_opcoes: config.cidades_opcoes || ["Senador Canedo", "Goiânia", "Aparecida de Goiânia", "Outros"]
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover esta configuração?")) return;
    try {
      await axios.delete(`${API}/public-form-configs/${id}`);
      toast.success("Configuração removida");
      loadConfigs();
    } catch (error) {
      toast.error("Erro ao remover configuração");
    }
  };

  const resetForm = () => {
    setEditingConfig(null);
    setForm({
      filial_id: "",
      nome_formulario: "Cadastro Público",
      ativo: true,
      campos_padrao: { ...DEFAULT_STANDARD_FIELDS },
      campos_personalizados: [],
      cidades_opcoes: ["Senador Canedo", "Goiânia", "Aparecida de Goiânia", "Outros"]
    });
  };

  const toggleFieldVisibility = (fieldKey) => {
    setForm(prev => ({
      ...prev,
      campos_padrao: {
        ...prev.campos_padrao,
        [fieldKey]: {
          ...prev.campos_padrao[fieldKey],
          visivel: !prev.campos_padrao[fieldKey].visivel
        }
      }
    }));
  };

  const toggleFieldRequired = (fieldKey) => {
    setForm(prev => ({
      ...prev,
      campos_padrao: {
        ...prev.campos_padrao,
        [fieldKey]: {
          ...prev.campos_padrao[fieldKey],
          obrigatorio: !prev.campos_padrao[fieldKey].obrigatorio
        }
      }
    }));
  };

  // Custom Fields
  const openCustomFieldDialog = (field = null) => {
    if (field) {
      setEditingCustomField(field);
      setCustomFieldForm({ ...field });
    } else {
      setEditingCustomField(null);
      setCustomFieldForm({
        id: "",
        label: "",
        tipo: "texto",
        obrigatorio: false,
        opcoes: [],
        ordem: form.campos_personalizados.length
      });
    }
    setCustomFieldDialog(true);
  };

  const saveCustomField = () => {
    if (!customFieldForm.label) {
      toast.error("Informe o nome do campo");
      return;
    }

    const fieldToSave = {
      ...customFieldForm,
      id: customFieldForm.id || `custom_${Date.now()}`
    };

    if (editingCustomField) {
      setForm(prev => ({
        ...prev,
        campos_personalizados: prev.campos_personalizados.map(f =>
          f.id === editingCustomField.id ? fieldToSave : f
        )
      }));
    } else {
      setForm(prev => ({
        ...prev,
        campos_personalizados: [...prev.campos_personalizados, fieldToSave]
      }));
    }

    setCustomFieldDialog(false);
    setEditingCustomField(null);
    setCustomFieldForm({
      id: "",
      label: "",
      tipo: "texto",
      obrigatorio: false,
      opcoes: [],
      ordem: 0
    });
  };

  const removeCustomField = (fieldId) => {
    setForm(prev => ({
      ...prev,
      campos_personalizados: prev.campos_personalizados.filter(f => f.id !== fieldId)
    }));
  };

  // City options
  const addCidade = () => {
    if (!newCidade.trim()) return;
    if (form.cidades_opcoes.includes(newCidade.trim())) {
      toast.error("Cidade já existe");
      return;
    }
    setForm(prev => ({
      ...prev,
      cidades_opcoes: [...prev.cidades_opcoes, newCidade.trim()]
    }));
    setNewCidade("");
  };

  const removeCidade = (cidade) => {
    setForm(prev => ({
      ...prev,
      cidades_opcoes: prev.cidades_opcoes.filter(c => c !== cidade)
    }));
  };

  const getFormUrl = (filialId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/cadastro-publico?unidade=${filialId}`;
  };

  const copyFormUrl = (filialId) => {
    navigator.clipboard.writeText(getFormUrl(filialId));
    toast.success("Link copiado!");
  };

  const getBranchName = (filialId) => {
    const branch = branches?.find(b => b.id === filialId);
    return branch?.nome || "Unidade";
  };

  if (!isAdmin()) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted-foreground">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Configuração do Formulário Público</h1>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-form-config">
          <Plus className="h-4 w-4 mr-2" />
          Nova Configuração
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Carregando...</p>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma configuração de formulário criada</p>
            <p className="text-sm text-muted-foreground mt-2">
              Crie uma configuração para personalizar o formulário de cadastro público de cada unidade.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {configs.map(config => (
            <Card key={config.id} data-testid={`form-config-${config.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{config.nome_formulario}</h3>
                      <p className="text-sm text-muted-foreground">
                        Unidade: {getBranchName(config.filial_id)}
                      </p>
                    </div>
                    <Badge variant={config.ativo ? "default" : "secondary"}>
                      {config.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => copyFormUrl(config.filial_id)}
                      title="Copiar link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => window.open(getFormUrl(config.filial_id), '_blank')}
                      title="Abrir formulário"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(config)}
                      data-testid={`edit-config-${config.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => handleDelete(config.id)}
                      data-testid={`delete-config-${config.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {config.campos_personalizados?.length || 0} campos personalizados
                  </Badge>
                  <Badge variant="outline">
                    {config.cidades_opcoes?.length || 0} cidades
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? "Editar Configuração" : "Nova Configuração de Formulário"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unidade *</Label>
                <Select
                  value={form.filial_id}
                  onValueChange={(v) => setForm({ ...form, filial_id: v })}
                  disabled={!!editingConfig}
                >
                  <SelectTrigger data-testid="select-branch">
                    <SelectValue placeholder="Selecione a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches?.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nome do Formulário</Label>
                <Input
                  value={form.nome_formulario}
                  onChange={(e) => setForm({ ...form, nome_formulario: e.target.value })}
                  placeholder="Cadastro Público"
                  data-testid="form-name"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.ativo}
                onCheckedChange={(checked) => setForm({ ...form, ativo: checked })}
                data-testid="form-active"
              />
              <Label>Formulário ativo</Label>
            </div>

            {/* Standard Fields */}
            <div className="space-y-3">
              <h3 className="font-medium">Campos Padrão</h3>
              <div className="grid gap-2">
                {Object.entries(form.campos_padrao).map(([key, field]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => toggleFieldVisibility(key)}
                        className={`p-1 rounded ${field.visivel ? 'text-primary' : 'text-muted-foreground'}`}
                      >
                        {field.visivel ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <span className={!field.visivel ? "text-muted-foreground line-through" : ""}>
                        {field.label || key}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">Obrigatório</Label>
                      <Switch
                        checked={field.obrigatorio}
                        onCheckedChange={() => toggleFieldRequired(key)}
                        disabled={!field.visivel}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Campos Personalizados</h3>
                <Button type="button" size="sm" variant="outline" onClick={() => openCustomFieldDialog()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              {form.campos_personalizados.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum campo personalizado</p>
              ) : (
                <div className="grid gap-2">
                  {form.campos_personalizados.map((field) => (
                    <div
                      key={field.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="font-medium">{field.label}</span>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {FIELD_TYPES.find(t => t.value === field.tipo)?.label || field.tipo}
                            </Badge>
                            {field.obrigatorio && (
                              <Badge variant="outline" className="text-xs">Obrigatório</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => openCustomFieldDialog(field)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeCustomField(field.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* City Options */}
            <div className="space-y-3">
              <h3 className="font-medium">Opções de Cidade</h3>
              <div className="flex gap-2">
                <Input
                  value={newCidade}
                  onChange={(e) => setNewCidade(e.target.value)}
                  placeholder="Nova cidade"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCidade())}
                />
                <Button type="button" variant="outline" onClick={addCidade}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.cidades_opcoes.map((cidade) => (
                  <Badge key={cidade} variant="secondary" className="pl-3 pr-1 py-1.5">
                    {cidade}
                    <button
                      type="button"
                      className="ml-2 hover:text-destructive"
                      onClick={() => removeCidade(cidade)}
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-form-config">
                {editingConfig ? "Salvar" : "Criar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog open={customFieldDialog} onOpenChange={setCustomFieldDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCustomField ? "Editar Campo" : "Novo Campo Personalizado"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Campo *</Label>
              <Input
                value={customFieldForm.label}
                onChange={(e) => setCustomFieldForm({ ...customFieldForm, label: e.target.value })}
                placeholder="Ex: Profissão"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo do Campo</Label>
              <Select
                value={customFieldForm.tipo}
                onValueChange={(v) => setCustomFieldForm({ ...customFieldForm, tipo: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {customFieldForm.tipo === "selecao" && (
              <div className="space-y-2">
                <Label>Opções (uma por linha)</Label>
                <textarea
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={customFieldForm.opcoes?.join("\n") || ""}
                  onChange={(e) => setCustomFieldForm({
                    ...customFieldForm,
                    opcoes: e.target.value.split("\n").filter(o => o.trim())
                  })}
                  placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch
                checked={customFieldForm.obrigatorio}
                onCheckedChange={(checked) => setCustomFieldForm({ ...customFieldForm, obrigatorio: checked })}
              />
              <Label>Campo obrigatório</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCustomFieldDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={saveCustomField}>
                {editingCustomField ? "Salvar" : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
